import React, { useState } from "react";
import { useLocation } from "react-router-dom";

import { useInstallCoach } from "./useInstallCoach";
import { useInstallPrompt } from "./useInstallPrompt";
import { isInstallRoute } from "./install-coach";

const ShareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 16V4M8 8l4-4 4 4M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" />
  </svg>
);

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-5 h-5 shrink-0 rounded-full bg-[color:var(--d-accent-soft)] text-[color:var(--d-accent-deep)] font-mono text-[11px] font-semibold flex items-center justify-center">
        {n}
      </span>
      <span className="text-[13px] text-content-secondary">{children}</span>
    </div>
  );
}

// Shared modal shell — dim scrim + bottom card. `data-design` so the `--d-*`
// tokens resolve even on the login page (which doesn't set it on <body>).
// `onBackdrop` is a SOFT close (hide for this session) — tapping outside must
// not permanently dismiss the prompt; only the explicit "Maybe later" does.
function Shell({ onBackdrop, children }: { onBackdrop: () => void; children: React.ReactNode }) {
  return (
    <>
      <div
        data-testid="install-coach-backdrop"
        className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-[4px]"
        onClick={onBackdrop}
        aria-hidden
      />
      <div data-design="refined-teal" className="lg:hidden fixed left-0 right-0 bottom-[calc(55px+var(--sab))] z-50 px-4 pb-3">
        <div className="bg-surface-primary border border-[color:var(--d-rule)] rounded-xl shadow-modal p-4 flex flex-col gap-3">
          {children}
        </div>
      </div>
    </>
  );
}

function Header() {
  return (
    <div className="flex items-start gap-3">
      <img src="/logo192.png" alt="" aria-hidden className="w-10 h-10 rounded-[9px] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold text-content-primary tracking-[-0.01em]">
          Add Autumn to your Home Screen
        </div>
        <p className="text-[12.5px] text-content-secondary mt-0.5">
          A faster, full-screen, app-like experience.
        </p>
      </div>
    </div>
  );
}

export function InstallCoach() {
  const { visible: iosVisible, dismiss, dismissed } = useInstallCoach();
  const { canInstall, promptInstall } = useInstallPrompt();
  const { pathname } = useLocation();
  // Soft close (backdrop tap) — hides the prompt for the rest of this app
  // session (PwaRuntime stays mounted, so it won't re-pop as the user navigates)
  // and resets on the next full load/reopen. Deliberately NOT persisted: an
  // accidental tap-outside must not permanently dismiss it (that's "Maybe later").
  const [closed, setClosed] = useState(false);
  const softClose = () => setClosed(true);

  if (closed) return null;

  // iOS Safari — manual Share → Add to Home Screen steps (no native prompt).
  if (iosVisible) {
    return (
      <Shell onBackdrop={softClose}>
        <Header />
        <div className="flex flex-col gap-2 pl-0.5">
          <Step n={1}>
            Tap the Share icon <span className="inline-flex align-middle text-content-tertiary"><ShareIcon /></span>
          </Step>
          <Step n={2}>
            Choose <span className="font-semibold text-content-primary">Add to Home Screen</span>
          </Step>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="self-end min-h-[36px] px-3 text-[13px] font-semibold text-content-secondary"
        >
          Maybe later
        </button>
      </Shell>
    );
  }

  // Chrome/Edge — native install prompt available. Login screen only, matching
  // the iOS coach. `canInstall` implies not-already-installed, so no standalone
  // check needed here.
  if (canInstall && isInstallRoute(pathname) && !dismissed) {
    return (
      <Shell onBackdrop={softClose}>
        <Header />
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={dismiss}
            className="min-h-[40px] px-3 text-[13px] font-semibold text-content-secondary"
          >
            Maybe later
          </button>
          <button
            type="button"
            onClick={promptInstall}
            className="min-h-[40px] px-4 rounded-md bg-content-primary text-white text-[14px] font-semibold"
          >
            Install
          </button>
        </div>
      </Shell>
    );
  }

  return null;
}
